import { scrollIntoView } from '@theme/scrolling';

class BoostReview extends HTMLElement {
  constructor() {
    super();
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-boost-review-track]');
    this.previousButton = this.querySelector('[data-boost-review-previous]');
    this.nextButton = this.querySelector('[data-boost-review-next]');
    this.status = this.querySelector('[data-boost-review-status]');

    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.previousButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
    this.updateControls();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
    clearTimeout(this.announcementTimer);
  }

  handleScroll() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.updateControls());
  }

  handlePrevious() {
    this.scrollByCard(-1);
  }

  handleNext() {
    this.scrollByCard(1);
  }

  handleBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('[data-boost-review-card]');
    if (!(card instanceof HTMLElement) || !this.track?.contains(card)) return;

    scrollIntoView(card, {
      ancestor: this.track,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('[data-boost-review-card]');
    if (!card) return;

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const directionMultiplier = styles.direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({
      left: direction * directionMultiplier * (card.getBoundingClientRect().width + gap),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });

    clearTimeout(this.announcementTimer);
    this.announcementTimer = setTimeout(
      () => this.announcePosition(),
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350
    );
  }

  announcePosition() {
    if (!this.status) return;

    const cards = [...this.track.querySelectorAll('[data-boost-review-card]')];
    const card = cards[0];
    if (!card) return;

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const cardWidth = card.getBoundingClientRect().width;
    const step = cardWidth + gap;
    const firstVisible = Math.min(cards.length - 1, Math.max(0, Math.round(Math.abs(this.track.scrollLeft) / step)));
    const visibleCount = Math.max(1, Math.round(this.track.clientWidth / step));
    const lastVisible = Math.min(cards.length, firstVisible + visibleCount);

    this.status.textContent =
      firstVisible + 1 === lastVisible
        ? `Showing review ${lastVisible} of ${cards.length}.`
        : `Showing reviews ${firstVisible + 1} through ${lastVisible} of ${cards.length}.`;
  }

  updateControls() {
    const maximumScroll = this.track.scrollWidth - this.track.clientWidth;
    const currentScroll = Math.abs(this.track.scrollLeft);
    const hasOverflow = maximumScroll > 2;
    const isAtStart = currentScroll <= 2;
    const isAtEnd = currentScroll >= maximumScroll - 2;

    this.previousButton.disabled = !hasOverflow || isAtStart;
    this.nextButton.disabled = !hasOverflow || isAtEnd;
    this.querySelector('[data-boost-review-controls]')?.toggleAttribute('hidden', !hasOverflow);
  }
}

if (!customElements.get('boost-review')) {
  customElements.define('boost-review', BoostReview);
}
